<script setup lang="ts">
   import { ref } from 'vue';
   import { SiteRouteNames } from '~/types/types';
   import { useScrollNav } from '~/composables/useScrollNav';
   import { useIpaNavigation } from '../composables/useIpaNavigation';

   const ipaNavListRef = ref<{ openMenu: () => void } | null>(null);
   const { isVisible, isScrolled } = useScrollNav();
   const { visibleItems } = useIpaNavigation();

   function openMobileMenu() {
      if (ipaNavListRef.value) {
         ipaNavListRef.value.openMenu();
      }
   }
</script>

<template>
   <div
      class="fixed top-0 z-50 flex w-full flex-col items-center justify-center bg-white pb-3 transition-all duration-300 lg:px-3"
      :class="
         isVisible
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-2 opacity-0'
      "
      style="box-shadow: 1px 1px 16px 0 rgba(0, 0, 0, 0.24)">
      <div class="w-full max-w-home-page-header-content">
         <!-- Top centered logo - desktop only, hidden when scrolled -->
         <div
            class="hidden py-4 transition-all duration-300 lg:flex lg:items-center lg:justify-center"
            :class="[
               isScrolled
                  ? 'lg:max-h-0 lg:overflow-hidden lg:py-0 lg:opacity-0'
                  : 'lg:max-h-20 lg:opacity-100',
            ]">
            <NuxtLink :to="{ name: SiteRouteNames.home }">
               <TextLogo variant="centered" />
            </NuxtLink>
         </div>

         <nav class="flex w-full items-center p-3 lg:justify-center lg:p-0">
            <!-- Left logo - appears when scrolled -->
            <NuxtLink
               :to="{ name: SiteRouteNames.home }"
               class="flex transition-all duration-300 lg:py-2"
               :class="[
                  !isScrolled
                     ? 'lg:max-w-0 lg:overflow-hidden lg:opacity-0'
                     : 'lg:mr-6 lg:max-w-full lg:opacity-100',
               ]">
               <TextLogo variant="left" title-size="text-lg" />
            </NuxtLink>

            <!-- Desktop Menu -->
            <ul class="ml-auto hidden items-center gap-3 lg:ml-0 lg:flex">
               <template v-for="item in visibleItems" :key="item.label">
                  <li>
                     <NuxtLink
                        :to="item.to"
                        :title="item.title"
                        class="flex cursor-pointer items-center justify-center whitespace-nowrap border border-white bg-brand-secondary px-7 py-3 uppercase text-white transition duration-200 hover:brightness-125">
                        {{ item.label }}
                     </NuxtLink>
                  </li>
               </template>
               <li>
                  <NuxtLink
                     :to="{ name: SiteRouteNames.platform }"
                     title="Enter the Polo Development Platform"
                     class="flex cursor-pointer items-center justify-center whitespace-nowrap border border-brand-primary bg-brand-primary px-7 py-3 uppercase text-white transition-colors duration-200 hover:bg-brand-primary hover:text-white">
                     Enter Platform
                  </NuxtLink>
               </li>
            </ul>

            <!-- Mobile Menu Button -->
            <button
               @click="openMobileMenu"
               class="ml-auto flex items-center justify-center border border-white bg-brand-secondary px-4 py-3 text-white lg:hidden"
               aria-label="Open mobile menu">
               <svg
                  class="size-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                     stroke-linecap="round"
                     stroke-linejoin="round"
                     stroke-width="2"
                     d="M4 6h16M4 12h16M4 18h16"></path>
               </svg>
            </button>
         </nav>
      </div>
   </div>

   <!-- Mobile Menu (Hidden by default, controlled by Mmenu) -->
   <div class="lg:hidden">
      <IpaNavList ref="ipaNavListRef" />
   </div>
</template>

<style scoped></style>
